import httpClient from 'hub-http/clients/apiClient';
import { useEffect, useState } from 'react';
import { createRpcClientV2 } from 'rpc-client-utils';
import { getProductInventoryRpc } from '../../__generated__/chirp/com/hubspot/buying/rpc/ProductRpc';
import { getUpgradeConfigWithPurchaseMotionsRpc } from '../../__generated__/chirp/com/hubspot/upgrade/management/rpc/UpgradeConfigInAppRpc';
import { getIsHubSpotOnCreditsPilot } from '../_core/common/eligibility/getIsHubSpotOnCreditsPilot';
import { logRavenException } from '../_core/utils/errors';
import { mapProductForHubSpotOnCredits } from '../_core/utils/mapProductForHubSpotOnCredits';
import { trackApiFailure, UIAUMetrics } from '../_core/utils/metricUtils';
import { validateUpgradeBannerDescriptions, validateUpgradePointType } from '../_core/utils/upgradeConfigValidators';
import { warnArchivedUpgradePointUse } from '../ums/utils';
import { useDisplayBannerOverride } from './useDisplayBannerOverride';
const rpcClient = createRpcClientV2({
  hubHttpClient: httpClient
});
export const useFetchUpgradeBannerConfig = (upgradeKey, limit, usage, displayThreshold) => {
  const [fetchedBannerDetails, setFetchedBannerDetails] = useState({
    loading: true,
    error: null
  });
  const displayBannerOverride = useDisplayBannerOverride();
  useEffect(() => {
    if (!upgradeKey || upgradeKey === '') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFetchedBannerDetails({
        loading: false,
        error: new Error('Upgrade key is empty')
      });
      return;
    }
    if (!displayBannerOverride && typeof usage === 'number' && typeof displayThreshold === 'number' && usage < displayThreshold) {
      setFetchedBannerDetails({
        loading: false,
        error: null
      });
      return;
    }
    const fetchStartTime = performance.now();
    const upgradeConfigPromise = rpcClient.call(getUpgradeConfigWithPurchaseMotionsRpc, {
      request: {
        upgradeKey,
        limit,
        amountUsed: usage,
        shouldIncludePurchaseMotions: false
      }
    });
    getIsHubSpotOnCreditsPilot().then(isInPilot => {
      if (isInPilot) {
        return Promise.all([upgradeConfigPromise, rpcClient.call(getProductInventoryRpc, {})]).then(([upgradeConfigResponse, productInventory]) => {
          var _productInventory$pro, _productInventory$pro2;
          const ownedHSoCApiNames = (_productInventory$pro = (_productInventory$pro2 = productInventory.products) === null || _productInventory$pro2 === void 0 ? void 0 : _productInventory$pro2.map(product => product.productApiName)) !== null && _productInventory$pro !== void 0 ? _productInventory$pro : [];
          const originalUpgradeConfig = upgradeConfigResponse.upgradeConfigProduct;
          const hsocMappedUpgradeConfig = originalUpgradeConfig ? mapProductForHubSpotOnCredits(originalUpgradeConfig, ownedHSoCApiNames) : originalUpgradeConfig;
          return hsocMappedUpgradeConfig;
        });
      } else {
        return upgradeConfigPromise.then(upgradeConfigResponse => {
          return upgradeConfigResponse.upgradeConfigProduct;
        });
      }
    }).then(upgradeConfig => {
      validateUpgradePointType(upgradeConfig);
      validateUpgradeBannerDescriptions(upgradeConfig);
      setFetchedBannerDetails({
        loading: false,
        error: null,
        data: {
          upgradeConfig
        }
      });
      if (upgradeConfig.status === 'ARCHIVED') {
        warnArchivedUpgradePointUse(upgradeConfig.upgradeKey);
      }
    }).catch(error => {
      var _error$cause, _error$message, _ref, _error$status, _error$cause2;
      setFetchedBannerDetails({
        loading: false,
        error
      });
      let message;
      if (((_error$cause = error.cause) === null || _error$cause === void 0 ? void 0 : _error$cause.status) === 404) {
        message = `${upgradeKey}: upgrade point may not exist in the UMS.`;
      } else if ((_error$message = error.message) !== null && _error$message !== void 0 && _error$message.includes('Validation error')) {
        message = error.message;
      } else {
        message = `${upgradeKey}: unexpected error fetching upgrade banner config`;
      }
      logRavenException({
        customMessage: message,
        error,
        uniqueId: upgradeKey,
        limit,
        usage,
        fingerprint: ['use-fetch-upgrade-banner-config']
      });
      trackApiFailure({
        caller: 'useFetchUpgradeBannerConfig',
        statusCode: (_ref = (_error$status = error === null || error === void 0 ? void 0 : error.status) !== null && _error$status !== void 0 ? _error$status : error === null || error === void 0 || (_error$cause2 = error.cause) === null || _error$cause2 === void 0 ? void 0 : _error$cause2.status) !== null && _ref !== void 0 ? _ref : 'unknown',
        upgradeKey
      });
    }).finally(() => {
      UIAUMetrics.timer('upgrade-banner-fetch-duration').update(Math.round(performance.now() - fetchStartTime));
    });
  }, [upgradeKey, limit, usage, displayThreshold, displayBannerOverride]);
  return fetchedBannerDetails;
};